import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import { createPortal } from 'react-dom';

import cls from 'classnames';

import _noop from 'lodash/noop';

import Button from '@ott/button';
import ChevronDownIcon from '@ott/icon-chevron-down';
import InputTrigger from '@ott/input-trigger';
import { l10n } from '@ott/l10n';
import Overlay from '@ott/overlay';

import { OFFSET_FROM_SIDE_BORDER, OFFSET_FROM_TRIGGER } from './constants';

import styles from './Travellers.scss';

const mainLocator = 'Travellers';

class Travellers extends PureComponent {
  static propTypes = {
    triggerValue: PropTypes.node,
    travellersList: PropTypes.node.isRequired,
    footer: PropTypes.node,

    childrenClassNames: PropTypes.shape({
      root: PropTypes.string,
      container: PropTypes.string,
      body: PropTypes.string,
      input: PropTypes.string,
    }),
    dataLocators: PropTypes.shape({
      container: PropTypes.string,
      input: PropTypes.string,
      overlay: PropTypes.string,
      applyButton: PropTypes.string,
    }),

    isInvalid: PropTypes.bool,
    isDisabled: PropTypes.bool,
    hasDoneButton: PropTypes.bool,
    withArrowIcon: PropTypes.bool,
    usePortalOverlay: PropTypes.bool,
    isAbsoluteOverlayPosition: PropTypes.bool,

    label: PropTypes.string,
    placeholder: PropTypes.string,
    overlayPlacement: PropTypes.oneOf(['top', 'right', 'bottom', 'left']),
    triggerAdditionalInfo: PropTypes.string,

    onOpen: PropTypes.func,
    onFocus: PropTypes.func,
    onBlur: PropTypes.func,

    specificLocators: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
    deviceInfo: PropTypes.shape({
      isTouchDevice: PropTypes.bool,
    }),
  };

  static defaultProps = {
    triggerValue: '',
    footer: '',

    placeholder: 'Путешественники',
    overlayPlacement: 'left',
    triggerAdditionalInfo: '',
    childrenClassNames: {},
    dataLocators: {},

    onOpen: _noop,
    onFocus: _noop,
    onBlur: _noop,

    isInvalid: false,
    isDisabled: false,
    hasDoneButton: false,
    withArrowIcon: false,
    usePortalOverlay: false,
    isAbsoluteOverlayPosition: false,

    specificLocators: [],
    deviceInfo: {},
  };

  constructor(props) {
    super(props);

    this.state = {
      isOpen: false,
    };

    this.containerRef = React.createRef();
    this.overlayRef = React.createRef();
  }

  componentDidUpdate() {
    if (this.state.isOpen) {
      this.bindOutsideClicks();
    } else {
      this.unbindOutsideClicks();
    }
  }

  componentDidMount() {
    window.addEventListener('resize', this.handleResize);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize);

    clearTimeout(this._closeTO);
    this.unbindOutsideClicks();

    if (this.portalRoot) {
      document.body.removeChild(this.portalRoot);
    }
    this.portalRoot = null;
  }

  bindOutsideClicks() {
    if (!this._bound) {
      const {
        deviceInfo: { isTouchDevice },
      } = this.props;
      const eventName = isTouchDevice ? 'touchend' : 'click';

      document.addEventListener(eventName, this.handleClickOutside);
      this._bound = true;
    }
  }

  unbindOutsideClicks() {
    if (this._bound) {
      const {
        deviceInfo: { isTouchDevice },
      } = this.props;
      const eventName = isTouchDevice ? 'touchend' : 'click';

      document.removeEventListener(eventName, this.handleClickOutside);
      this._bound = false;
    }
  }

  setTriggerRef = (node) => {
    this.triggerRef = node;
  };

  handleResize = () => {
    const { isOpen } = this.state;
    if (isOpen) {
      this.setState({ position: this.getOverlayPosition() });
    }
  };

  getOverlayPosition = () => {
    const { usePortalOverlay } = this.props;
    if (!usePortalOverlay) {
      return {};
    }

    if (!this.triggerRef) {
      return {};
    }

    const inputPosition = this.triggerRef.getBoundingClientRect();

    return {
      top: (window.scrollY || window.pageYOffset) + inputPosition.bottom + OFFSET_FROM_TRIGGER,
      left: inputPosition.left - OFFSET_FROM_SIDE_BORDER,
      right: window.innerWidth - inputPosition.right - OFFSET_FROM_SIDE_BORDER,
    };
  };

  handleBubble = (e) => {
    if (e) {
      e.stopPropagation();
      if (e.nativeEvent && e.nativeEvent.stopImmediatePropagation) {
        e.nativeEvent.stopImmediatePropagation();
      }
    }
  };

  handleClick = (e) => {
    const { isDisabled } = this.props;

    if (isDisabled) {
      return;
    }

    const { isOpen } = this.state;

    this.handleBubble(e); // todo check

    const overlayNode = this.overlayRef.current;
    if (overlayNode && overlayNode.contains(e.target)) {
      return;
    }

    if (isOpen && !this._preventCloseByTriggerClick) {
      this.handleCloseAsync(e);
    } else {
      this.handleOpen(e);
    }
  };

  handleOpen = (e) => {
    clearTimeout(this._closeTO);
    this.setState({
      isOpen: true,
      position: this.getOverlayPosition(),
    });

    const { onFocus, onOpen } = this.props;
    onOpen(e);
    onFocus(e);
  };

  handleClose = (e) => {
    this.setState({ isOpen: false });

    const { onBlur } = this.props;
    onBlur(e);
  };

  handleFocus = (e) => {
    // кейс: инпут активен > клик по адресной строке > клик по инпуту (focus > click)
    // без этого привента handleClick будет скрывать дропдаун
    this._preventCloseByTriggerClick = true;
    setTimeout(() => {
      this._preventCloseByTriggerClick = false;
    }, 200);

    const { isDisabled } = this.props;

    if (isDisabled) {
      return null;
    }

    this.handleOpen(e);
  };

  handleBlur = () => this.handleCloseAsync();

  handleCloseAsync = () => {
    // используется для кейсов, когда клик фокусирует элементу,
    // но при этому должен закрывать компонент
    this._closeTO = setTimeout(this.handleClose, 30);
  };

  handleClickOutside = (e) => {
    const containerNode = this.containerRef.current;
    const overlayNode = this.overlayRef.current;

    if (
      (containerNode && containerNode.contains(e.target)) ||
      (overlayNode && overlayNode.contains(e.target))
    ) {
      return;
    }

    this.handleCloseAsync();
  };

  handleEnter = (e) => {
    if (!this.state.isOpen) {
      this.handleOpen(e);
    }
  };

  handleKeyDown = (e) => {
    switch (e.key) {
      case 'Enter':
      case ' ':
        this.handleEnter(e);
        break;
      case 'Escape':
        this.handleBlur(e);
        break;
      default:
      // do nothing
    }
  };

  renderIcon() {
    const { isOpen } = this.state;
    const { withArrowIcon } = this.props;

    if (!withArrowIcon) {
      return null;
    }

    return <ChevronDownIcon className={cls(styles.arrow, isOpen && styles.arrowOpen)} />;
  }

  renderPlaceholder() {
    const { placeholder } = this.props;

    return <span className={styles.placeholder}>{placeholder}</span>;
  }

  renderTrigger = () => {
    const { isOpen } = this.state;
    const {
      childrenClassNames,
      dataLocators,
      label,
      triggerAdditionalInfo,
      triggerValue,
      isInvalid,
      isDisabled,
    } = this.props;

    const placeholderNode = !triggerValue && this.renderPlaceholder();

    return (
      <InputTrigger
        parentInputRef={this.setTriggerRef}
        className={childrenClassNames.input}
        specificLocators={dataLocators.input}
        isActive={isOpen}
        isInvalid={isInvalid}
        hasBottomLine={false}
        hideThrobber={true}
        errorPlacement="top"
        name="travellers-trigger"
        iconNode={this.renderIcon()}
        label={label}
        onFocus={this.handleFocus}
      >
        <div className={cls(isDisabled && styles.triggerDisabled)}>
          {placeholderNode}
          {triggerValue}
          {triggerAdditionalInfo}
        </div>
      </InputTrigger>
    );
  };

  renderOverlay() {
    const {
      childrenClassNames,
      dataLocators,
      travellersList,
      footer,
      hasDoneButton,
      usePortalOverlay,
    } = this.props;
    const { position } = this.state;

    return (
      <div
        ref={this.overlayRef}
        style={position}
        className={cls(
          styles.container,
          childrenClassNames.container,
          usePortalOverlay && styles.portalContainer
        )}
        data-locator={cls('travellersContainer', dataLocators.overlay)}
        tabIndex="-1"
        onMouseDown={this.handleOptionFocus}
        onFocus={this.handleOptionFocus}
        onBlur={this.handleOptionBlur}
      >
        <div className={cls(styles.body, childrenClassNames.body)}>
          {travellersList}
          {footer && <div className={styles.footer}>{footer}</div>}
          {hasDoneButton && (
            <div className={styles.doneButton}>
              <Button
                specificLocators={dataLocators.applyButton}
                withFullWidth={true}
                size="medium"
                onClick={this.handleCloseAsync}
              >
                {l10n('travellersControl.done')}
              </Button>
            </div>
          )}
        </div>
      </div>
    );
  }

  renderContainer() {
    const { isOpen } = this.state;
    const { usePortalOverlay, overlayPlacement, isAbsoluteOverlayPosition } = this.props;

    if (!isOpen) {
      return null;
    }

    const travellersNode = this.renderOverlay();

    if (usePortalOverlay) {
      if (!this.portalRoot) {
        this.portalRoot = document.createElement('div');
        document.body.appendChild(this.portalRoot);
      }

      return createPortal(travellersNode, this.portalRoot);
    }

    return (
      <Overlay
        show={true}
        container={this.containerRef.current}
        placement={overlayPlacement}
        isAbsolutePosition={isAbsoluteOverlayPosition}
      >
        {travellersNode}
      </Overlay>
    );
  }

  render() {
    const { specificLocators, childrenClassNames, dataLocators } = this.props;

    const handlers = {
      onMouseDown: this.handleClick,
      onKeyDown: this.handleKeyDown,
      onFocus: this.handleFocus,
      onBlur: this.handleBlur,
    };

    return (
      <div
        ref={this.containerRef}
        className={cls(styles.Travellers, childrenClassNames.root)}
        data-locator={cls(mainLocator, specificLocators, dataLocators.container)}
        tabIndex="0"
        {...handlers}
      >
        {this.renderTrigger()}
        {this.renderContainer()}
      </div>
    );
  }
}

export default Travellers;
